<template>
  <TText size="lg" tag="h1" variant="semibold" classes="mb-4">{{
    t('auth.registrationTraveler.email.title')
  }}</TText>
  <div v-if="currentState === 'ERROR'" class="my-4">
    <ApiResponseError :api-response-error="apiResponseError" />
  </div>
  <UForm
    ref="form"
    :schema="schema"
    :state="state"
    class="space-y-3"
    @submit="onSubmit"
  >
    <UFormField required name="emailAddress">
      <UInput
        v-model="state.emailAddress"
        type="text"
        inputmode="email"
        size="lg"
        :placeholder="t('auth.register.email')"
        :aria-label="t('auth.register.email')"
        :title="t('auth.register.email-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField required name="emailAddressRepeat">
      <UInput
        v-model="state.emailAddressRepeat"
        type="text"
        inputmode="email"
        size="lg"
        :placeholder="t('auth.register.emailConfirm')"
        :aria-label="t('auth.register.emailConfirm')"
        :title="t('auth.register.emailConfirm-title')"
        onpaste="return false"
        ondrop="return false"
        autocomplete="email"
      />
    </UFormField>
    <UFormField required name="password">
      <TPassword
        v-model="state.password"
        :placeholder="t('auth.register.password')"
        :title="t('auth.register.password-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField required name="firstName">
      <UInput
        v-model="state.firstName"
        size="lg"
        :placeholder="t('auth.register.name')"
        :aria-label="t('auth.register.name')"
        :title="t('auth.register.name-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField required name="lastName">
      <UInput
        v-model="state.lastName"
        size="lg"
        :placeholder="t('auth.register.surnames')"
        :aria-label="t('auth.register.surnames')"
        :title="t('auth.register.surnames-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField required name="phone">
      <UInput
        v-model="state.phone"
        size="lg"
        type="tel"
        :placeholder="t('auth.register.phone')"
        :aria-label="t('auth.register.phone')"
        :title="t('auth.register.phone-title')"
        autocomplete="on"
      />
    </UFormField>
    <UFormField v-if="provinces && provinces.length > 0" name="provinceId">
      <UInputMenu
        v-model="state.provinceId"
        :items="provinces"
        size="lg"
        :placeholder="`${t('auth.register.province') + ' ' + t('form.optional')}`"
        :aria-label="t('auth.register.province')"
        :title="t('auth.register.province-title')"
        label-key="name"
        value-key="value"
        autocomplete="on"
      />
    </UFormField>
    <UFormField name="allowComms">
      <UCheckbox
        v-model="state.allowComms"
        class="mt-5"
        :label="t('auth.register.allowComms')"
      />
    </UFormField>
    <UFormField name="privacyPolicy">
      <UCheckbox v-model="state.privacyPolicy" required>
        <template #label>
          <i18n-t keypath="auth.confirmation.label" tag="span" scope="global">
            <template #useConditions>
              <ULink
                :active="true"
                target="_blank"
                :href="t('auth.confirmation.useConditionsUrl')"
                >{{ t('auth.confirmation.useConditions') }}</ULink
              >
            </template>
            <template #privacyPolicy>
              <ULink
                :active="true"
                target="_blank"
                :href="t('auth.confirmation.privacyPolicyUrl')"
                >{{ t('auth.confirmation.privacyPolicy') }}</ULink
              >
            </template>
          </i18n-t>
        </template>
      </UCheckbox>
    </UFormField>
    <UFormField name="recaptcha">
      <TRecaptcha
        ref="vueRecaptchaRef"
        v-model="state.recaptcha"
        class="mt-6 mb-3"
      />
    </UFormField>

    <div class="flex flex-col items-center">
      <UButton
        color="neutral"
        class="mt-2 mb-6"
        variant="solid"
        size="xl"
        type="submit"
        :disabled="!isValid"
        :loading="currentState === 'SENDING'"
      >
        {{ t('auth.register.submit') }}
      </UButton>
    </div>
  </UForm>
</template>

<script setup lang="ts">
import { z } from 'zod'
import { browserFetch as fetchSignUp } from '~~/modules/api/rurality/v3/users/sign-up'
import { browserFetch as fetchLogin } from '~~/modules/api/rurality/v3/users/login'
import { paramsSchema } from '~~/shared/api/rurality/v3/users/sign-up/paramsSchema'
import { useZodValidator } from '~~/modules/form/composables/useZodValidator'
import { scrollToTop } from '~/composables/useAnimations'
import { browserFetch as getProvinces } from '~~/modules/api/rurality/v3/provinces/detail'
import { useToProvinces } from '~/composables/transformers/useToProvinces'
import { signUpOrigin } from '~~/modules/auth/constants'
import loggedUserRedirect from '~~/modules/auth/composables/useAuthRedirect'

interface ChildComponent {
  triggerReset(): void
}

const props = defineProps<{
  isModal?: boolean
}>()

const emit = defineEmits(['login-success'])

const apiResponseError: Ref<ApiResponseError | null> = ref(null)
const vueRecaptchaRef = ref<ChildComponent | null>(null)

const provincesResponse = await getProvinces()
const provincesRaw = provincesResponse.success
  ? provincesResponse.success.data.provinces
  : []

const provinces = useToProvinces().fromRegister(provincesRaw)

const recaptchaReset = () => {
  state.recaptcha = undefined
  vueRecaptchaRef.value?.triggerReset()
}

const {
  validateEmail,
  validatePhone,
  validatePassword,
  validateRequiredCheckbox,
} = useZodValidator()

const form = ref()

const schemaRaw = paramsSchema.extend({
  firstName: z.string().min(2).max(30),
  lastName: z.string().min(2).max(30),
  phone: validatePhone()
    .transform(value => (value === '' ? null : value))
    .transform(value => (value === undefined ? null : value)),
  provinceId: z
    .number()
    .int()
    .optional()
    .nullable()
    .transform(value => (value === undefined ? null : value)),
  emailAddress: validateEmail(),
  password: validatePassword(),
  privacyPolicy: validateRequiredCheckbox(),
})

const state = reactive({
  firstName: undefined,
  lastName: undefined,
  phone: undefined,
  emailAddress: undefined,
  emailAddressRepeat: undefined,
  provinceId: undefined,
  password: undefined,
  allowComms: false,
  privacyPolicy: false as false,
  recaptcha: undefined,
  facebookId: null,
  googleId: null,
  appleId: null,
  userType: 'TRAVELER' as const,
  signUpOrigin: signUpOrigin.PAGINA_REGISTRO_MAIL,
})

const schema = useNuxtUiFormRepeat({
  form,
  state,
  schema: schemaRaw,
  parentFieldName: 'emailAddress',
  repeatFieldName: 'emailAddressRepeat',
  message: t('zodI18nCustom.email_repeat'),
})

const states = {
  IDLE: 'IDLE',
  INVALID: 'INVALID',
  VALID: 'VALID',
  SENDING: 'SENDING',
  ERROR: 'ERROR',
}

const currentState: Ref<keyof typeof states> = ref('IDLE')

const isValid = computed(() => {
  return schema.safeParse(state).success
})

async function onSubmit(event: any) {
  const result = schema.safeParse(event.data)

  if (result.success) {
    currentState.value = 'SENDING'

    const parsedData = result.data

    const signUp = await fetchSignUp(parsedData)

    if (signUp.success && signUp.success.data) {
      const login = await fetchLogin({
        emailAddress: parsedData.emailAddress,
        password: parsedData.password,
      })
      if (login.success && login.success.data) {
        if (props.isModal) {
          emit('login-success')
        } else {
          navigateTo(
            loggedUserRedirect({
              userType: login.success.data.userType,
              userId: login.success.data.userId,
            }),
            {
              external: true,
            },
          )
        }
      } else {
        apiResponseError.value = login.error
        currentState.value = 'ERROR'
        scrollToTop()
        recaptchaReset()
      }
    } else {
      apiResponseError.value = signUp.error
      currentState.value = 'ERROR'
      scrollToTop()
      recaptchaReset()
    }
  }
}
</script>
